import {History} from 'history'
import { CabinetService, ChangeResult } from './CabinetService';
import { Translatable } from './TranslationService';
import { ErrorFlag, UserRight } from './WebApiClient';

export interface NavPage extends Translatable {
    readonly url: string;
    readonly translationKey: string;
    readonly translationDefault: string;
    readonly requiredRights?: UserRight[];
    //TODO more NavPages
}
export interface EditorNavPage extends NavPage {
    /**1-based index */
    readonly editorIndex: number;
    hasErrors(cabinetService: CabinetService): boolean;
}

export module NavPage {

    export const ProductLine: EditorNavPage = {
        editorIndex: 1,
        url: "/",
        translationKey: "route productline",
        translationDefault: "Produktlinje",
        hasErrors: (_) => false,
    }

    export const Dimensions: EditorNavPage = {
        editorIndex: 2,
        url: "/dimensions",
        translationKey: "route dimensions",
        translationDefault: "Dimensioner",
        hasErrors: (_) => false,
    }

    export const Corpus: EditorNavPage = {
        editorIndex: 3000,
        url: "/corpus",
        translationKey: "route corpus",
        translationDefault: "Korpus",
        hasErrors: (_) => false,
    }

    export const DoorTypes: EditorNavPage = {
        editorIndex: 3,
        url: "/doorTypes",
        translationKey: "route doortypes",
        translationDefault: "Dørtype",
        hasErrors: (_) => false,
    };

    export const Doors: EditorNavPage = {
        editorIndex: 4,
        url: "/doors",
        translationKey: "route doors",
        translationDefault: "Døre",
        hasErrors: (_) => false,
    };

    export const Interior: EditorNavPage = {
        editorIndex: 5,
        url: "/interior",
        translationKey: "route interior",
        translationDefault: "Interiør",
        hasErrors: (cabinetService) => cabinetService.getErrorFlags() !== ErrorFlag.None,
    };

    export const ExtraProducts: EditorNavPage = {
        editorIndex: 6,
        url: "/extraProducts",
        translationKey: "route extraProducts",
        translationDefault: "Løssalg",
        hasErrors: (_) => false,
    }

    export const PieceList: EditorNavPage = {
        editorIndex: 7,
        url: "/piecelist",
        translationKey: "route piecelist",
        translationDefault: "Stykliste",
        hasErrors: (_) => false,
    }

    

    export module Admin {
        export const Material: NavPage = {
            url: "/admin/materials",
            translationKey: "route admin materials",
            translationDefault: "Materialer",
            requiredRights: [UserRight.EditMaterials]
        };

        export const Chain: NavPage = {
            url: "/admin/chains",
            translationKey: "route admin chains",
            translationDefault: "Brands",
            requiredRights: [UserRight.EditChains]
        }

        export const ProductLine: NavPage = {
            url: "/admin/productLines",
            translationKey: "route admin productLines",
            translationDefault: "Produktlinjer",
            requiredRights: [UserRight.EditProductLines]
        }

        export const Product: NavPage = {
            url: "/admin/products",
            translationKey: "route admin products",
            translationDefault: "Produkter",
            requiredRights: [UserRight.EditProducts]
        }

        export const InteriorMaterial: NavPage = {
            url: "/admin/interiorMaterials",
            translationKey: "route admin interior materials",
            translationDefault: "Interiør-materialer",
            requiredRights: [UserRight.EditMaterials],
        }

        export const Door: NavPage = {
            url: "/admin/doors",
            translationKey: "route admin doors",
            translationDefault: "Døre",
            requiredRights: [UserRight.EditProducts]
        }

        export const Translation: NavPage = {
            url: "/admin/translations",
            translationKey: "route admin translations",
            translationDefault: "Oversættelser",
            requiredRights: [UserRight.EditTranslations]
        }

        export const Misc: NavPage = {
            url: "/admin/misc",
            translationKey: "route admin misc",
            translationDefault: "Diverse Admin",
            requiredRights: [UserRight.StartSync],
        }

        export const Pages: NavPage[] = [
            Material,
            InteriorMaterial,
            Chain,
            ProductLine,
            Product,
            Door,
            Translation,

            Misc,
        ];
    }

    export module User {
        export const CabinetList: NavPage = {
            translationKey: "route user solution list",
            translationDefault: "Gemte Løsninger",
            url: "/user/solutions",
            requiredRights: [UserRight.LoadSolution]
        }

        export const UserSearch: NavPage = {
            translationKey: "route user search",
            translationDefault: "Kundesøgning",
            url: "/user/search",
            requiredRights: [UserRight.LoadChainSolution]
        }

        export const Pages: NavPage[] = [
            CabinetList,
            UserSearch,
        ];
    }

    export const EditorPages: EditorNavPage[] = [
        ProductLine,
        Dimensions,
        //Corpus,
        DoorTypes,
        Doors,
        Interior,
        ExtraProducts,
        PieceList,
    ];
}



export interface INavService {
/**
 * Returns the previous page in the flow, or undefined if there is no obvious previous page. Does not navigate.
 * @param current
 */
    prev(current: EditorNavPage): EditorNavPage | undefined;
    navigatePrev(current: EditorNavPage): void;
    navigatePrevAvailable(current: EditorNavPage): void;

    /**
     * Returns the next page in the flow, or undefined if there is no obvious next page. Does not navigate.
     * @param current
     */
    next(current: EditorNavPage): EditorNavPage | undefined;
    navigateNext(current: EditorNavPage, force?: true): void;
    navigateNextAvailable(current: EditorNavPage): void;

    canNavigateTo(page: NavPage | undefined): page is NavPage;

    navigate(page: NavPage, force?: true): Promise<ChangeResult>;

    getUrl(page: NavPage): string;
}

export class NavService implements INavService {
    constructor(
        private readonly cabinetService: CabinetService,
        private readonly history?: History,
    ) {

    }
    navigatePrev(current: EditorNavPage): void{
        this.navigate(this.prev(current));
    }
    navigatePrevAvailable(current: EditorNavPage): void {
        this.navigate(this.prevAvailable(current));
    }
    navigateNext(current: EditorNavPage, force?: true): void {
        this.navigate(this.next(current), force);
    }

    navigateNextAvailable(current: EditorNavPage): void {
        this.navigate(this.nextAvailable(current));
    }

    prev(current: EditorNavPage): EditorNavPage | undefined {
        if (!current?.editorIndex)
            return;
        return NavPage.EditorPages[current.editorIndex - 2];
    }
    prevAvailable(current: EditorNavPage): EditorNavPage | undefined {
        let prev = this.prev(current);
        while (prev) {
            if (this.canNavigateTo(prev)) {
                return prev;
            }
            prev = this.prev(prev);
        }
        return undefined;
    }

    next(current: EditorNavPage): EditorNavPage | undefined {
        if (!current?.editorIndex)
            return;
        return NavPage.EditorPages[current.editorIndex ];
    }
    nextAvailable(current: EditorNavPage): EditorNavPage | undefined {
        let next = this.next(current);
        while (next) {
            if (this.canNavigateTo(next)) {
                return next;
            }
            next = this.next(next);
        }
        return undefined;
    }
    getUrl(page: NavPage): string {
        return page.url;
    }

    canNavigateTo(page: NavPage | undefined): page is NavPage {
        if (!page)
            return false;

        if (page === NavPage.ProductLine)
            return true;

        if (page === NavPage.PieceList)
            return true;

        if (page === NavPage.ExtraProducts)
            return true;

        if (NavPage.Admin.Pages.find(p => p === page))
            return true;

        if (NavPage.User.Pages.find(p => p === page))
            return true;


        if (this.cabinetService.cabinet.productLineId === null)
            return false;
        if (page === NavPage.Dimensions)
            return true;

        if (this.cabinetService.cabinet.position === null)
            return false;
        if (page === NavPage.Corpus)
            return true;

        if (this.cabinetService.cabinet.roof === null)
            return false;
        if (this.cabinetService.cabinet.bottom === null)
            return false;
        if (this.cabinetService.cabinet.sidePanel === null)
            return false;
        if (page === NavPage.DoorTypes)
            return true;

        if (page === NavPage.Interior)
            return true;

        if (page === NavPage.Doors)
            return true;

        return false;

    }

    async navigate(page: NavPage | undefined, force?:true): Promise<ChangeResult> {
        if (!force && !this.canNavigateTo(page))
            return { success: false };
        if (!page)
            return { success: false };
        if (page === NavPage.Dimensions) {
            const result = await this.cabinetService.initDimensions();
            if (!result.success)
                return result;
        }
        else if (page === NavPage.Doors) {
            const result = await this.cabinetService.initDoors();
            if (!result.success)
                return result;

        }
        this.history?.push(page.url);
        return { success: true };
    }

    
}
